import * as _ from "lodash";
import numeral from "numeral";

import { PropertyModel as Model } from "../../model/propertyFinder";
import { viewerOptions } from "../../viewer";
import { ViewerUtils } from "../../common/viewerUtils";
import * as SmartDrawings from "../../smartDrawings";

/**
 * A property panel that displays properties according to a filtering criteria.
 */
export class SwecoPropertyPanel extends Autodesk.Viewing.Extensions.ViewerPropertyPanel {

    private viewer: Autodesk.Viewing.GuiViewer3D;
    private propertyFinder: Model.PropertyFinder;
    private propertyCriteria: Model.PropertyCriterion[];
    private defaultTitle: string;
    private titleSelector: Model.TitleSelector;
    private currentNodeId: any;
    private currentModelId: number;
    private currentAggregateSelection: Autodesk.Viewing.AggregateSelectionResult[];

    private previousSelection: number[] = [];
    private lastSelection: number;

    constructor(viewer: Autodesk.Viewing.GuiViewer3D) {
        super(viewer);
        this.viewer = viewer;
        this.propertyFinder = new Model.PropertyFinder(this.getPropertiesFromModel);
    }

    resetProperties(show) {
        if (!show) {
            this.currentNodeId = null;
            this.currentModelId = null;
        }
    }

    /**
      * Called by the superclass on node selection for combo models
      */
    async requestAggregatedNodeProperties(aggregatedSelection: Autodesk.Viewing.AggregateSelectionResult[]) {
        this.currentAggregateSelection = aggregatedSelection;
        this.currentModelId = null;
        if (!Array.isArray(aggregatedSelection)) {
            return;
        }

        let ids = this.combineSelectionIdArrays(aggregatedSelection);

        // Have to check last selection, because this callback runs before selection event
        const diff = _.difference(ids, this.previousSelection);
        this.lastSelection = diff.length > 0 ? diff[0] : ids[0];

        this.previousSelection = ids;

        const nodeId = this.lastSelection;
        this.currentModelId = this.findModelId(nodeId, aggregatedSelection);

        const otherIds = _.filter(ids, s => s !== nodeId);
        const propertiesAndTitle = await this.propertyFinder.getNodeProperties(
            new SmartDrawings.ModelNode(this.currentModelId, nodeId),
            otherIds, this.propertyCriteria, this.titleSelector);

        await this.addExternalData(propertiesAndTitle.properties, nodeId, this.findModelId(nodeId, aggregatedSelection));

        this.setProperties(propertiesAndTitle.properties);
        this.resizeToContent(null);
        if (this.defaultTitle == null) {
            if (propertiesAndTitle.title != null) {
                this.setTitle(propertiesAndTitle.title, null);
            } else {
                this.setTitle(nodeId.toString(), null);
            }
        } else {
            this.setTitle(this.defaultTitle, null);
        }
        this.currentNodeId = nodeId;
        this.currentAggregateSelection = aggregatedSelection;
    }

    private combineSelectionIdArrays(selections: Autodesk.Viewing.AggregateSelectionResult[]): number[] {
        let ids = [];
        selections.forEach((s) => {
            ids = ids.concat(s.selection);
        });
        return ids;
    }

    private findModelId(nodeId: number, selections: Autodesk.Viewing.AggregateSelectionResult[]): number {
        let id: number = null;
        for (let s of selections) {
            if (s.selection.find(i => i === nodeId)) {
                id = s.model.id as number;
                break;
            }
        }

        return id;
    }

    /**
     * The getter is implemented here to decouple the property finder from the viewer.
     */
    private getPropertiesFromModel = (modelNode: SmartDrawings.IModelNode): Promise<Autodesk.Viewing.Property[]> => {
        try {
            return new Promise((resolve, reject) => {
                ViewerUtils.getModel(modelNode.modelId, this.viewer).getProperties(
                    modelNode.nodeId,
                    result => resolve(result.properties),
                error => {
                    if (error)
                        return reject(error);

                    return resolve([]);
                });
            });
        } catch (e) {
            console.log(e);
            return null;
        }
        
    }

    setPropertyRules(criteria: Model.PropertyCriterion[], title: string, titleSelector: Model.TitleSelector) {
        this.propertyCriteria = criteria;
        this.defaultTitle = title;
        this.titleSelector = titleSelector;
        if (this.currentNodeId != null) {
            (this as any).setNodeProperties(this.currentNodeId);
            this.requestAggregatedNodeProperties(this.currentAggregateSelection);
        }
    }

    private async findNodeData(externalData: SmartDrawings.ExternalData, nodeId, modelId) {

        // Maybe it's actually a node, try getting that data.
        const { nodes } = externalData;
        // Assuming that there is only a single real model and custom model.
        if (nodes != null && modelId > 1) {
            return nodes.propertyValues[nodeId];
        }

        const idPropInfo = externalData.idProperty;
        let idProp: Autodesk.Viewing.Property = null;
        if (idPropInfo != null) {
            const props = await ViewerUtils.getNodeProperties(nodeId, this.viewer);
            idProp = ViewerUtils.findProperty(props.properties, idPropInfo.displayCategory, idPropInfo.displayName);
        }

        const guid = idProp != null
            ? idProp.displayValue : externalData.idProperty != null
            ? _.findKey(externalData.paramsToDbIds, value => value === nodeId) : viewerOptions.idMap.guid(null, nodeId);
        const data = externalData.propertyValues[guid];
        if (data != null) {
            return data;
        }

        // Or try to find data in the parent.
        const parentId = await ViewerUtils.getNodeParent(nodeId, this.viewer);
        if (parentId != null) {
            return this.findNodeData(externalData, parentId, modelId);
        }

        return null;
    }

    private async addExternalData(properties: Autodesk.Viewing.Property[], nodeId, modelId) {
        const { externalData } = viewerOptions as SmartDrawings.Model;
        
        if (externalData != null) {
            const data = await this.findNodeData(externalData, nodeId, modelId);
            if (data == null) {
                return;
            }

            const propertyNames = _.keys(data);
            for (let propName of propertyNames) {
                const propData: string = data[propName];
                const definition = externalData.propertyDefinitions[propName] || externalData.nodes.propertyDefinitions[propName];
                const type = this.getType(definition);
                const value = definition.type === "double" ? (propData?.toString() || "").replace(",", ".") : propData;
                const [format, unit] = (definition.format || "").split(" ");
                if (type !== 50) {
                    const property: Autodesk.Viewing.Property = {
                        attributeName: null,
                        displayCategory: definition.category || "Other data",
                        displayName: definition.description,
                        displayValue: !_.isEmpty(format) ? numeral(value).format(format) : value || "",
                        units: unit,
                        hidden: false,
                        type: type
                    }
                    if (property.displayName != null) {
                        properties.push(property);
                    }
                }
            }
        }
    }

    private getType(propertyDefinition) {
        //return 20;

        switch (propertyDefinition["type"]) {
            case "string":
                return 20;
            case "boolean":
                return 1;
            case "integer":
                return 2;
            case "double":
                return 3;
            case "tooltip":
                return 50;
            default:
                return 0;
        }
    }
}